import React, { useState, useEffect, useContext } from "react";
import { useParams, useNavigate } from "react-router-dom";

import { toast } from "../../services/toast";

import { Paper } from "@mui/material";

import { makeStyles } from 'tss-react/mui';

import ContactDrawer from "../ContactDrawer";
import MessageInput from "../MessageInputCustom/";
import SugerenciaOtraLinea from "../SugerenciaOtraLinea";
import TicketHeader from "../TicketHeader";
import TicketInfo from "../TicketInfo";
import TicketActionButtons from "../TicketActionButtonsCustom";
import MessagesList from "../MessagesList";
import api from "../../services/api";
import { ReplyMessageProvider } from "../../context/ReplyingMessage/ReplyingMessageContext";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { TagsContainer } from "../TagsContainer";
import AiReviewBanner from "../AiReviewBanner";
import FacebookCommentBanner from "../FacebookCommentBanner";
import { socketConnection } from "../../services/socket";

const drawerWidth = 320;

const useStyles = makeStyles()((theme) => ({
  root: {
    display: "flex",
    height: "100%",
    position: "relative",
    overflow: "hidden",
  },

  mainWrapper: {
    flex: 1,
    height: "100%",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    borderTopLeftRadius: 0,
    borderBottomLeftRadius: 0,
    borderLeft: "0",
    marginRight: -drawerWidth,
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
  },

  mainWrapperShift: {
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginRight: 0,
  },
}));

const Ticket = () => {
  const { ticketId } = useParams();
  const navigate = useNavigate();
  const { classes, cx } = useStyles();

  const { user } = useContext(AuthContext);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [contact, setContact] = useState({});
  const [ticket, setTicket] = useState({});
  const [windowStatus, setWindowStatus] = useState({
    canStillReply: true,
    hasCustomerMessage: true,
    loaded: false,
  });
  const [searchOpen, setSearchOpen] = useState(false);
  const [starredOnly, setStarredOnly] = useState(false);
  const [isContactTyping, setIsContactTyping] = useState(false);

  useEffect(() => {
    setLoading(true);
    setWindowStatus({ canStillReply: true, hasCustomerMessage: true, loaded: false });
    setIsContactTyping(false);
    const delayDebounceFn = setTimeout(() => {
      const fetchTicket = async () => {
        try {
          const { data } = await api.get("/tickets/u/" + ticketId);
          const { queueId } = data;
          const { queues, profile } = user;

          const queueAllowed = queues.find((q) => q.id === queueId);
          
          if (queueAllowed === undefined && profile !== "admin" && profile !== "supervisor") {
          }

          setContact(data.contact);
          setTicket(data);
          setLoading(false);
        } catch (err) {
          setLoading(false);
          toastError(err);
        }
      };
      fetchTicket();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [ticketId, user, navigate]);

  useEffect(() => {
    if (!ticket.id) return undefined;

    const companyId = localStorage.getItem("companyId");
    const socket = socketConnection({ companyId });

    socket.on("connect", () => socket.emit("joinChatBox", `${ticket.id}`));

    socket.on(`company-${companyId}-ticket`, (data) => {
      if (data.action === "update") {
        setTicket(data.ticket);
      }

      if (data.action === "delete") {
        if (data.ticketId && data.ticketId !== ticket.id) return;

        if (data.bsuidMerge && data.redirectToTicketUuid) {
          toast.info("Este contacto se unificó con su ficha existente. Te llevamos a la conversación completa.");
          navigate(`/tickets/${data.redirectToTicketUuid}`);
          return;
        }

        toast.success("Se ha cerrado el ticket.");
        navigate("/tickets");
      }
    });

    socket.on(`company-${companyId}-contact`, (data) => {
      if (data.action === "update") {
        setContact((prevState) => {
          if (prevState.id === data.contact?.id) {
            return { ...prevState, ...data.contact };
          }
          return prevState;
        });
      }
    });

    socket.on(`company-${companyId}-presence`, (data) => {
      if (data.ticketId === ticket.id) {
        setIsContactTyping(data.isTyping);
      }
    });

    return () => {
      socket.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketId, ticket.id, navigate]);

  const handleDrawerOpen = () => {
    setDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
  };

  const renderTicketInfo = () => {
    if (ticket.user !== undefined) {
      return (
        <TicketInfo
          contact={contact}
          ticket={ticket}
          onClick={handleDrawerOpen}
          isContactTyping={isContactTyping}
        />
      );
    }
  };

  const renderMessagesList = () => {
    return (
      <>
        <MessagesList
          ticket={ticket}
          ticketId={ticket.id}
          isGroup={ticket.isGroup}
          onWindowStatusChange={setWindowStatus}
          searchOpen={searchOpen || starredOnly}
          starredOnly={starredOnly}
          onCloseSearch={() => {
            setSearchOpen(false);
            setStarredOnly(false);
          }}
        ></MessagesList>

        {}
        <SugerenciaOtraLinea ticket={ticket} />
        <FacebookCommentBanner ticket={ticket} />

        <MessageInput
          ticketId={ticket.id}
          ticketStatus={ticket.status}
          contact={contact}
          channel={ticket.channel}
          whatsappId={ticket.whatsappId}
          templateRequired={
            ticket.channel === "whatsapp_cloud" &&
            windowStatus.loaded &&
            !windowStatus.canStillReply
          }
        />
      </>
    );
  };

  return (
    <div className={classes.root} id="drawer-container">
      <Paper
        variant="outlined"
        elevation={0}
        className={cx(classes.mainWrapper, {
          [classes.mainWrapperShift]: drawerOpen,
        })}
      >
        <TicketHeader loading={loading}>
          {renderTicketInfo()}
          <TicketActionButtons
            ticket={ticket}
            contact={contact}
            searchOpen={searchOpen}
            onToggleSearch={() => {
              setStarredOnly(false);
              setSearchOpen((prev) => !prev);
            }}
            starredOnly={starredOnly}
            onToggleStarred={() => {
              setSearchOpen(false);
              setStarredOnly((prev) => !prev);
            }}
          />
        </TicketHeader>
        <Paper>
          <TagsContainer ticket={ticket} />
        </Paper>
        <AiReviewBanner ticket={ticket} />
        <ReplyMessageProvider>{renderMessagesList()}</ReplyMessageProvider>
      </Paper>
      <ContactDrawer
        open={drawerOpen}
        handleDrawerClose={handleDrawerClose}
        contact={contact}
        loading={loading}
        ticket={ticket}
      />
    </div>
  );
};

export default Ticket;
